'use client';

import type {
  BookAnotherProviderEventOptions,
  CachedLiveSlotsDiscrepancyEventOptions,
  ChangedSortEventOptions,
  ClickedProviderLinkEventOptions,
  CompletedFormEventOptions,
  ComponentClickEvent,
  DropdownSearchEvent,
  EhrRedirectEventOptions,
  NavigationClickEvent,
  SchedulingEventOptions,
  SchedulingPageViewEvent,
  SearchClickEvent,
  SearchEvent,
  StandardEventPayloadOptions,
  TrackingFn,
  UpdatedSearchFilterEventOptions,
} from '../../lib/analytics/types';
import mixpanelPlugin from '@analytics/mixpanel';
import Analytics, { AnalyticsInstance } from 'analytics';
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getActiveDtreeQuestionId } from '@provider-directory/contexts/DecisionTreeContext';
import { useIsPrimaryCareSpecialty } from '@provider-directory/hooks/useIsPrimaryCareSpecialty';
import { LocationResource } from '@provider-directory/types/locations.contracts';
import {
  AppointmentProvider,
  ProviderResource,
} from '@provider-directory/types/providers.contracts';
import {
  ConversionContext,
  SchedulingFlowType,
} from '../../lib/analytics/conversions';
import {
  buildBookAnotherProviderPayload,
  buildCachedLiveSlotsDiscrepancyPayload,
  buildClickedProviderLinkPayload,
  buildDTreeAnswerSubmittedPayload,
  buildDTreeQuestionShownPayload,
  buildEhrRedirectPayload,
  buildEventPayload,
  buildSchedulingEventPayload,
} from '../../lib/analytics/payload-builders';
import {
  addOpenedModalProvider,
  getCurrentPage,
  getOpenedModalProviders,
  getPreviousEvent,
  getPreviousPage,
  setCurrentPage,
  setPreviousEvent,
} from '../../lib/analytics/sequence';
import { CROSS_DOMAIN_PARAMS } from '../../lib/analytics/types';
import googleTagManager from './analytics-gtm-plugin';
import internalAnalyticsPlugin from './analytics-internal-plugin';

// Re-export public API so existing imports from this module continue to work
export type { ConversionType } from '../../lib/analytics/conversions';
export {
  CROSS_DOMAIN_PARAMS,
  INTERNAL_ONLY_EVENTS,
  type ChangedSortEventOptions,
  type ClickedProviderLinkEventOptions,
  type CompletedFormEventOptions,
  type EhrRedirectEventOptions,
  type EventPayload,
  type FormStep,
  type SchedulingEventOptions,
  type StandardEventPayloadOptions,
} from '../../lib/analytics/types';
export {
  buildEventPayload,
  buildConversionSearchFilterFields,
} from '../../lib/analytics/payload-builders';

const QUESTION_ID_ENRICHED_EVENTS = new Set<string>([
  'CLICKED_TO_CALL',
  'CLICKED_EHR_REDIRECT',
]);

/** Name Mixpanel receives for page views (`pageEvent` below). */
const PAGE_VIEW_EVENT_NAME = 'PAGE_VIEW';

/**
 * Scheduling scenes pass an explicit `path`/`url` because `window.location` can
 * lag behind a soft navigation; everything else reports the live pathname.
 */
const resolvePagePath = (payload: Record<string, any>): string => {
  if (typeof payload.path === 'string' && payload.path) {
    return payload.path;
  }
  if (typeof payload.url === 'string' && payload.url) {
    try {
      return new URL(payload.url, window.location.origin).pathname;
    } catch {
      // malformed override — fall back to the live pathname
    }
  }
  return window.location.pathname;
};

export const removeTrackingParamsFromUrl = (
  urlParams: URLSearchParams,
): void => {
  CROSS_DOMAIN_PARAMS.forEach((param) => {
    urlParams.delete(param);
  });

  const queryString = urlParams.toString();
  const newUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;

  window.history.replaceState(window.history.state, '', newUrl);
};

export const registerCrossDomainTrackingParameters = (
  urlParams: URLSearchParams,
): void => {
  const mixpanelInstance = window.mixpanel;
  if (
    !mixpanelInstance ||
    typeof mixpanelInstance.register_once !== 'function'
  ) {
    return;
  }

  CROSS_DOMAIN_PARAMS.forEach((param) => {
    const value = urlParams.get(param);
    if (!value) {
      return;
    }

    mixpanelInstance.register_once({
      [param]: value,
    });
  });
};

interface TrackingFunctions {
  schedulingPageViewEvent: TrackingFn<SchedulingPageViewEvent>;
  navigationClickEvent: TrackingFn<NavigationClickEvent>;
  componentClickEvent: TrackingFn<ComponentClickEvent>;
  searchClickEvent: TrackingFn<SearchClickEvent>;
  searchEvent: TrackingFn<SearchEvent>;
  dropdownSearchEvent: TrackingFn<DropdownSearchEvent>;
  dTreeQuestionShownEvent: (options: {
    questionId: string;
    provider: AppointmentProvider;
    location?: LocationResource;
    specialtyBranch?: string;
    schedulingFlowType?: SchedulingFlowType;
  }) => void;
  dTreeAnswerSubmittedEvent: (options: {
    questionId: string;
    answer: string;
    provider: ProviderResource;
    location?: LocationResource;
    specialtyBranch?: string;
    reasonForVisit?: string;
    visitTypeId?: string;
    schedulingFlowType?: SchedulingFlowType;
  }) => void;
  clickedBookAnotherProviderEvent: (
    options: BookAnotherProviderEventOptions,
  ) => void;
  cachedLiveSlotsDiscrepancyEvent: (
    options: CachedLiveSlotsDiscrepancyEventOptions,
  ) => void;
  viewedProfileEvent: (
    options: StandardEventPayloadOptions & { provider: ProviderResource },
  ) => void;
  calledEvent: (options: StandardEventPayloadOptions) => void;
  completedFormEvent: (options: CompletedFormEventOptions) => void;
  changedSortEvent: (options: ChangedSortEventOptions) => void;
  directionsEvent: (options: StandardEventPayloadOptions) => void;
  clickedBioAnchorLinkEvent: (options: StandardEventPayloadOptions) => void;
  clickedProviderLinkEvent: (options: ClickedProviderLinkEventOptions) => void;
  openedFullModalEvent: (options: StandardEventPayloadOptions) => void;
  ehrRedirectEvent: (options: EhrRedirectEventOptions) => void;
  updatedSearchFilterEvent: (options: UpdatedSearchFilterEventOptions) => void;
  apptBookedSuccessEvent: (options: SchedulingEventOptions) => void;
  apptBookedErrorEvent: (options: SchedulingEventOptions) => void;
  slotUnavailableEvent: (options: SchedulingEventOptions) => void;
  schedulingErrorEvent: (options: SchedulingEventOptions) => void;
  conversionClickEvent: (options: ConversionContext) => void;
}

interface AnalyticsWithTrackingFunctions
  extends AnalyticsInstance,
    TrackingFunctions {
  /**
   * True once the plugin-bearing instance exists. Mount-triggered events wait
   * for this so they are not emitted against the throwaway instance rendered
   * before hydration — that fire is dropped but still advances the sequence,
   * which is what made events report themselves as `previousEvent`.
   */
  isReady: boolean;
}

const AnalyticsContext = createContext<
  AnalyticsWithTrackingFunctions | undefined
>(undefined);

export const useAnalytics = (): AnalyticsWithTrackingFunctions => {
  const analytics = useContext(AnalyticsContext);
  if (!analytics) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return analytics;
};

const AnalyticsProvider = ({
  children,
  mixpanelToken,
  gtmContainerId,
  isDev,
  isSynthetics,
  organizationCode,
}: PropsWithChildren<{
  mixpanelToken: string;
  gtmContainerId: string;
  isDev: boolean;
  isSynthetics: boolean;
  organizationCode?: string;
}>): ReactNode => {
  const [isMounted, setIsMounted] = useState<boolean>(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isPrimaryCare = useIsPrimaryCareSpecialty();

  const analyticsInstance = useMemo(
    () =>
      Analytics({
        app: 'provider-directory',
        plugins:
          isMounted && !isSynthetics
            ? [
                mixpanelPlugin({
                  options: {
                    api_host: isDev ? undefined : '/mp',
                    record_sessions_percent: 0,
                    record_heatmap_data: true,
                  },
                  token: mixpanelToken,
                  pageEvent: 'PAGE_VIEW',
                  customScriptSrc: isDev ? undefined : '/mp/lib.min.js',
                }),
                googleTagManager({
                  containerId: gtmContainerId,
                }),
                internalAnalyticsPlugin(),
              ]
            : [],
      }),
    [isMounted, isSynthetics, isDev, mixpanelToken, gtmContainerId],
  );

  const analytics: AnalyticsWithTrackingFunctions = useMemo(() => {
    analyticsInstance.on('ready', () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (CROSS_DOMAIN_PARAMS.some((p) => urlParams.has(p))) {
        registerCrossDomainTrackingParameters(urlParams);
        removeTrackingParamsFromUrl(urlParams);
      }
    });

    const enrichWithSequence = (
      eventName: string,
      payload: Record<string, any>,
    ) => {
      const previousEvent = getPreviousEvent();
      const previousPage = getPreviousPage();
      setPreviousEvent(eventName);
      const dtreeQuestionId = getActiveDtreeQuestionId();
      const shouldEnrichQuestionId =
        dtreeQuestionId !== null &&
        QUESTION_ID_ENRICHED_EVENTS.has(eventName) &&
        payload.questionId === undefined;
      return {
        ...(shouldEnrichQuestionId ? { questionId: dtreeQuestionId } : {}),
        ...payload,
        previousEvent,
        previousPage,
        organizationCode,
      };
    };

    // A page view reports the page being navigated away from, since its own
    // path becomes the current one as soon as the pointers advance.
    const enrichPageWithSequence = (payload: Record<string, any>) => {
      const previousEvent = getPreviousEvent();
      const previousPage = getCurrentPage();
      setPreviousEvent(PAGE_VIEW_EVENT_NAME);
      setCurrentPage(resolvePagePath(payload));
      return { ...payload, previousEvent, previousPage, organizationCode };
    };

    // Single enrichment site: every event reaches a plugin through these two
    // wrappers, including raw `analytics.track` / `analytics.page` call sites.
    // Until the provider has mounted, refuse to mutate the sequence — callers
    // that depend on `analytics` (404/500 banners) re-fire after the rebuild,
    // and a pre-ready write would make that second fire report itself.
    const track: AnalyticsInstance['track'] = (eventName, payload, ...rest) => {
      if (!isMounted) {
        return Promise.resolve();
      }
      return analyticsInstance.track(
        eventName,
        enrichWithSequence(eventName, payload ?? {}),
        ...rest,
      );
    };

    const page: AnalyticsInstance['page'] = (data, ...rest) => {
      if (!isMounted) {
        return Promise.resolve();
      }
      return analyticsInstance.page(
        enrichPageWithSequence(data ?? {}),
        ...rest,
      );
    };

    const analyticsWithTracking: AnalyticsWithTrackingFunctions = {
      ...analyticsInstance,
      track,
      page,
      isReady: isMounted,
      navigationClickEvent: (payload) => {
        void track('e_navigationClick', payload);
      },
      componentClickEvent: (payload) => {
        void track('e_componentClick', payload);
      },
      searchClickEvent: (payload) => {
        void track('e_searchClick', payload);
      },
      searchEvent: (payload) => {
        void track('e_search', payload);
      },
      dropdownSearchEvent: (payload) => {
        void track('e_dropdownSearch', payload);
      },

      dTreeQuestionShownEvent: (options) => {
        void track(
          'DTREE_QUESTION_SHOWN',
          buildDTreeQuestionShownPayload(options),
        );
      },
      dTreeAnswerSubmittedEvent: (options) => {
        void track(
          'SUBMITTED_DTREE_ANSWER',
          buildDTreeAnswerSubmittedPayload(options),
        );
      },
      schedulingPageViewEvent: (
        payload: Omit<SchedulingPageViewEvent, 'event'>,
      ) => {
        void page({
          ...payload,
          appType: 'provider-directory',
        });
      },
      clickedBookAnotherProviderEvent: (options) => {
        void track(
          'CLICKED_BOOK_ANOTHER_PROVIDER',
          buildBookAnotherProviderPayload(options),
        );
      },
      cachedLiveSlotsDiscrepancyEvent: (options) => {
        void track(
          'CACHED_LIVE_SLOTS_DISCREPANCY',
          buildCachedLiveSlotsDiscrepancyPayload(options),
        );
      },
      viewedProfileEvent: (options) => {
        void track('VIEWED_PROFILE', buildEventPayload(options, isPrimaryCare));
      },
      calledEvent: (options) => {
        void track(
          'CLICKED_TO_CALL',
          buildEventPayload(options, isPrimaryCare),
        );
      },
      completedFormEvent: (options) => {
        const { formStep, ...standardOptions } = options;
        const payload = buildEventPayload(standardOptions, isPrimaryCare);
        payload.formStep = formStep;
        void track('COMPLETED_FORM', payload);
      },
      changedSortEvent: (options) => {
        const { sortValue, previousSortValue, ...standardOptions } = options;
        const payload = buildEventPayload(standardOptions, isPrimaryCare);
        payload.sortValue = sortValue;
        if (previousSortValue) {
          payload.previousSortValue = previousSortValue;
        }
        void track('CHANGED_SORT', payload);
      },
      directionsEvent: (options) => {
        void track(
          'CLICKED_TO_GET_DIRECTIONS',
          buildEventPayload(options, isPrimaryCare),
        );
      },
      clickedBioAnchorLinkEvent: (options) => {
        void track(
          'CLICKED_PROVIDER_BIO_ANCHOR_LINKS',
          buildEventPayload(options, isPrimaryCare),
        );
      },
      clickedProviderLinkEvent: (options) => {
        void track(
          'CLICKED_PROVIDER_LINK',
          buildClickedProviderLinkPayload(options, isPrimaryCare),
        );
      },
      openedFullModalEvent: (options) => {
        const providerId = options.provider?.npi ?? '';
        if (getOpenedModalProviders().has(providerId)) {
          return;
        }
        addOpenedModalProvider(providerId);

        void track(
          'OPENED_FULL_MODAL',
          buildEventPayload(options, isPrimaryCare),
        );
      },
      ehrRedirectEvent: (options) => {
        void track(
          'CLICKED_EHR_REDIRECT',
          buildEhrRedirectPayload(options, isPrimaryCare),
        );
      },
      updatedSearchFilterEvent: ({
        filter,
        value,
        searchParams,
        linkContextPage,
        linkContextComponent,
      }) => {
        void track('UPDATED_SEARCH_FILTER', {
          filter,
          value,
          ...searchParams,
          appType: 'provider-directory',
          linkContextPage,
          linkContextComponent,
        });
      },
      apptBookedSuccessEvent: (options) => {
        void track('APPT_BOOKED_SUCCESS', buildSchedulingEventPayload(options));
      },
      apptBookedErrorEvent: (options) => {
        void track('APPT_BOOKED_ERROR', buildSchedulingEventPayload(options));
      },
      slotUnavailableEvent: (options) => {
        void track('SLOT_UNAVAILABLE', buildSchedulingEventPayload(options));
      },
      schedulingErrorEvent: (options) => {
        void track('SCHEDULING_ERROR', buildSchedulingEventPayload(options));
      },
      conversionClickEvent: (options) => {
        void track('CONVERSION', options);
      },
    };

    return analyticsWithTracking;
  }, [analyticsInstance, isMounted, organizationCode, isPrimaryCare]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
